// src/oura/tracker.ts
//
// The per-business execution tracker: the platform version of the internal
// GSheet master tracker (Bee Choo Origin reference, OT-2026-BCO-001). One
// tracker per business, generated from the assessment: a six-factor
// scoreboard (baseline, target, result), the four-week execution timeline
// (action, tool, verification gate, owner, status), and the detected-issues
// fix register mapping every crawl finding to where it gets fixed.
//
// Mock seam shaped like the future GET /assessments/{id}/tracker. Progress is
// deterministic per business so the demo is stable: some businesses are mid
// week 1, others deep into week 3.

import type { Business, ScoreSnapshot } from "./contract";
import type { AeoTaskDoneBy, AeoTaskImpact } from "@service/aeo";
import { getAudit } from "./audit";
import { dateAfter } from "./lib/oura";

export type TaskStatus = "not_started" | "in_progress" | "blocked" | "done";
export type TaskOwner = "ourteam" | "client";
export type RegisterStatus = "done" | "pending" | "blocked" | "out_of_scope";

export interface ScoreboardFactor {
    key: string;
    label: string;
    baseline: string;
    target: string;
    result: string | null; // null until re-measured (amber cell in the GSheet)
    note: string;
}

export interface TrackerTask {
    id: string;
    action: string;
    notes: string;
    tool: string;
    gate: string; // verification gate: how we confirm it is genuinely done
    owner: TaskOwner;
    status: TaskStatus;
    /** True when the work happens inside Oura (scan, crawl, AI probe): the
     *  platform flips the status itself when the run completes. Everything
     *  else is marked done manually. */
    auto: boolean;
    /**
     * What this task was generated to fix (ourteam-backend#3817) — a query the
     * business loses, or an audit check that failed. This is what closes the
     * loop: without it a task is a to-do item with no traceable cause, and the
     * fix drawer cannot say WHY the work is on the plan.
     *
     * Optional because the fixture generator does not produce it.
     */
    source?: { type: string; ref: string };
    /** The queries behind a content task, for a "write this page" brief. */
    sourceQueries?: string[];
    /**
     * Which template this task is an instance of. Stable by construction, which
     * is what lets a re-scan carry a task's progress across a rebuild — and what
     * a deliverable line joins on (#4795).
     *
     * `"contradiction"` is the one this frontend switches on: see `evidence`.
     */
    templateKey?: string | null;
    /**
     * Free-shaped by design — every rule that raises a task decides what its own
     * evidence is. `{}` is the normal case.
     *
     * On a `templateKey === "contradiction"` task it is a
     * `ContradictionEvidence` (ourteam-backend#4837, K-2): both quotes, the
     * engine, when it was said, and the citation K-1 verified. Read it with
     * `contradictionEvidence()` rather than casting at the callsite — a task
     * whose evidence is a different rule's shape must render as an ordinary
     * task, not as a half-filled contradiction card.
     */
    evidence?: Record<string, unknown>;
    /** When this task is due (ourteam-backend#4508). Always present on a live
     *  plan: it falls back to the block's milestone date, and `targetDateSource`
     *  says whether a person set it or it came from the block. */
    targetDate?: string | null;
    targetDateSource?: "task" | "block" | null;
    /** Set by the screen, not the wire: the check behind this task now reports
     *  zero pages, so there is nothing left to do. Rendered struck through
     *  rather than dropped, so the row closes instead of vanishing. */
    resolved?: boolean;
    /** The page this task acts on — what a generated fix would be written to. */
    targetPage?: string;
    cause?: string;
    /** What closed the task when the platform did rather than a person: a run
     *  that re-measured it, or a connector that proved it. Absent on a task
     *  somebody ticked done by hand. */
    closedBy?: TaskClosedBy;
    /** One of the three moments the client owes (`access`, `review`,
     *  `handover`, ourteam-backend#4476); null on every other task. */
    touchpoint?: string | null;
    /** Which channels the work moves. Stored by the backend and not yet
     *  serialized (handoff "Serialize task.helps"); absent means derive it from
     *  `source.type` with the backend's own rule. */
    helps?: TaskChannel[];
    /** When the task moved into done, and who moved it (handoff "Tracker:
     *  record when and by whom a task was done"). Absent on live today. */
    doneAt?: string | null;
    doneBy?: AeoTaskDoneBy | null;
    /** The same-method before and after for done work (same handoff). Absent
     *  on live today, and never derived here. */
    impact?: AeoTaskImpact | null;
    /**
     * Every page this task's audit finding flagged (ourteam-backend#4353).
     *
     * This is what makes a tracker row more than a category. "Add structured
     * data" is a heading; "Add structured data — 6 pages, here they are" is a
     * job someone can start. The BE sends it already filtered to pages that
     * still fail, with a pointer to any draft already generated for each.
     *
     * `undefined` = the payload predates #4353 or this is a fixture. `[]` = the
     * BE looked and this task genuinely has no page-scoped work (a scan, a
     * handover call). Those two must not be shown the same way: the first is
     * "we don't know", the second is "nothing to do".
     */
    affectedPages?: TaskAffectedPage[];
    affectedPageCount?: number;
}

/** A channel a task moves: the AI assistants, or Google search. */
export type TaskChannel = "ai" | "google";

/**
 * Who or what closed a task. An object on the wire: `verify_tasks_against_run`
 * and the auto-task flip write `{assessmentId, at, reason}`, the Search
 * Console check writes `{source: "gsc", at, reason}`.
 */
export interface TaskClosedBy {
    at: string;
    reason?: string;
    assessmentId?: string;
    source?: string;
}

/** One page under a tracker task — the join to the Site Audit's own findings. */
export interface TaskAffectedPage {
    url: string;
    title: string;
    /** open · new · regressed (still failing) — the BE has already filtered out
     *  fixed/verified/not_crawled, so this is here to LABEL a row, not to
     *  decide whether it belongs. */
    status?: string;
    /** The check's own column value for this page (status code, word count…). */
    meta?: string;
    /** An existing draft for this page + issue. Present = do not regenerate. */
    draftId?: string;
}

/** A block of the plan: Kickoff, then the four delivery weeks. */
export type TrackerBlock = 0 | 1 | 2 | 3 | 4;

/** The plan template this build of the FE renders. Compared against the
 *  tracker's stored `templateVersion` to offer a rebuild; keep in step with
 *  TRACKER_TEMPLATE_VERSION in the backend's aeo_tracker.py. */
export const CURRENT_TEMPLATE_VERSION = 2;

/** Every block, in plan order. The one list the move menu and any future
 *  block picker read, so they cannot disagree about whether Kickoff exists. */
export const TRACKER_BLOCKS: TrackerBlock[] = [0, 1, 2, 3, 4];

/** "Kickoff" or "Week 3". Kickoff is a named phase, not week zero, and printing
 *  it as a number is how the five-block plan first showed up wrong. Takes any
 *  number because a suggestion names its week as one. */
export function blockLabel(week: number): string {
    return week === 0 ? "Kickoff" : `Week ${week}`;
}

/** The heading for a block: "Kickoff" or "Week 2 · Content". Kickoff's own title
 *  IS "Kickoff", so the number-and-title form would repeat it. */
export function blockHeading(block: { week: TrackerBlock; title: string; label?: string }): string {
    // The BE's own label wins. Only it knows the spans, so a 3-week block reads
    // "Weeks 2 to 4" there while this function's arithmetic would still say
    // "Week 2" — which is what made a saved phase length look like it had not
    // saved at all (ourteam-backend#4517).
    //
    // The phase NAME leads and the span follows, because "Technical foundation"
    // is what the work is and "Weeks 2 to 4" is when it happens. A block running
    // three weeks titled "Week 2" was simply wrong.
    if (block.week === 0) return block.title || "Kickoff";
    const span = block.label && block.label !== block.title ? block.label : `Week ${block.week}`;
    return `${block.title} · ${span}`;
}

export interface TrackerWeek {
    /**
     * 0 is Kickoff (ourteam-backend#4476), 1-4 are the delivery weeks.
     *
     * The union used to stop at 4 while `adaptTracker` cast the wire value into
     * it, so a five-block plan type-checked and then rendered "Week 0".
     */
    week: TrackerBlock;
    title: string;
    goal: string;
    deliverable: string;
    dates: string;
    tasks: TrackerTask[];
    /** How many weeks this block spans (ourteam-backend#4517). Default 1, so a
     *  plan that predates it is five one-week blocks and nothing changes. */
    weeks?: number;
    /** "Kickoff", "Week 1", "Weeks 2 to 4" — computed by the BE from position
     *  and length. Preferred over `blockLabel()` when present, since only the
     *  BE knows the spans. */
    label?: string;
}

export interface FixRegisterRow {
    issueKey: string;
    issue: string;
    detectedWhere: string;
    fixedAt: string;
    status: RegisterStatus;
    note: string;
}

export interface BusinessTracker {
    reference: string;
    packageName: string;
    window: string;
    currentWeek: TrackerBlock;
    nextDeliverable: string;
    scoreboard: ScoreboardFactor[];
    weeks: TrackerWeek[];
    fixRegister: FixRegisterRow[];
    /** When the PLAN was built, not when the scan ran (ourteam-backend#4497).
     *  A plan can be rebuilt from the same scan, so the two differ. */
    generatedAt?: string | null;
    generatedFrom?: string | null;
    /** When the work actually starts (ourteam-backend#4477). Every date in the
     *  plan counts forward from it in working days. Null on a plan stored
     *  before the field existed. */
    startDate?: string | null;
    /** Whether a person chose that date or it was defaulted, plus the sentence
     *  saying so. A defaulted date must never be shown as a chosen one. */
    startDateSource?: "operator" | "default" | null;
    startDateNote?: string | null;
    /** Bumped when the plan template changes; a stored value below the current
     *  one is how the UI knows a rebuild is worth offering. */
    templateVersion?: number;
    /**
     * Work the rules propose and nobody has decided on yet (#4513).
     *
     * The plan is the template plus tasks derived from lost questions, and
     * everything else a client needs was invisible to it. Rules propose, a
     * human accepts; an accepted proposal is still generator-derived, so the
     * next rebuild reproduces it rather than deleting it the way a hand-typed
     * task would be deleted.
     *
     * Empty on a fixture and on any plan built before the queue existed.
     */
    suggestions?: TrackerSuggestion[];
}

/** One proposal awaiting a decision (ourteam-backend#4513). */
export interface TrackerSuggestion {
    id: string;
    rule: string;
    week: number;
    action: string;
    notes: string;
    /** The evidence sentence, with real integers. It is what makes a proposal
     *  worth reading rather than another row on a list, and it is the same
     *  sentence that reaches the client, so it is never hidden behind a hover. */
    why: string;
    evidence?: { urls?: string[]; titles?: string[] };
    state: "proposed" | "kept" | "dismissed";
}

function hashOf(s: string): number {
    return s.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 9973, 13);
}

// The standard four-week program, condensed from the master tracker sheet.
// Statuses are assigned at generation time from the business's progress point.
// Tools whose work runs inside Oura itself: their tracker rows complete
// automatically when the platform action finishes.
const AUTO_TOOLS = new Set(["Oura scan", "Oura crawl", "AI assistants"]);

const WEEK_TEMPLATE: Array<Omit<TrackerWeek, "tasks" | "dates"> & { tasks: Array<Omit<TrackerTask, "id" | "status" | "auto">> }> = [
    {
        week: 1,
        title: "Technical foundation",
        goal: "Make the site readable to AI engines. Slowest to recrawl work goes earliest.",
        deliverable: "All technical fixes live plus a progress note",
        tasks: [
            { action: "Confirm CMS admin access", notes: "Dedicated ourteam admin account, so access can be revoked cleanly at handover.", tool: "CMS admin", gate: "Dashboard loads with the full admin menu", owner: "client", touchpoint: "access" },
            { action: "Full site backup before any edit", notes: "Files plus database, one copy stored off server. Non negotiable safety net.", tool: "Backup plugin", gate: "Backup listed and a copy downloaded off server", owner: "ourteam" },
            { action: "Set up GSC and GA4 access", notes: "Verify the Search Console property and confirm analytics is collecting.", tool: "GSC / GA4", gate: "Property verified and GA4 receiving data", owner: "ourteam" },
            { action: "Lock the six factor baseline", notes: "Schema coverage, page labels, content depth, question coverage, visibility score, indexed pages.", tool: "Oura scan", gate: "Baseline recorded on the scoreboard", owner: "ourteam" },
            { action: "Capture the AI naming baseline", notes: "How ChatGPT and Gemini describe the business today. The starting line for week 4.", tool: "AI assistants", gate: "Answers saved as the visibility starting line", owner: "ourteam" },
            { action: "Full technical crawl audit", notes: "Surface 404s, redirect chains, missing titles and meta, thin content, orphan pages.", tool: "Oura crawl", gate: "Crawl export saved and the issue list triaged", owner: "ourteam" },
            { action: "Implement structured data", notes: "Organization, LocalBusiness and Review markup across the homepage and key templates.", tool: "CMS / schema", gate: "Markup validates with zero errors on every changed page", owner: "ourteam", source: { type: "check", ref: "no_schema" } },
            { action: "Optimise page labels", notes: "Unique title, unique meta description and a single clear H1 on each priority page.", tool: "CMS", gate: "Every priority page carries unique labels", owner: "ourteam", source: { type: "check", ref: "dup_titles" } },
            { action: "Regenerate robots.txt and XML sitemap", notes: "Declare the sitemap, exclude noindexed pages, resubmit so recrawling starts immediately.", tool: "CMS / GSC", gate: "Sitemap submitted and no important page blocked", owner: "ourteam", source: { type: "check", ref: "robots_missing" } },
        ],
    },
    {
        week: 2,
        title: "Content",
        goal: "Answer the questions customers actually ask AI engines.",
        deliverable: "Copy pack sent to the client for review",
        tasks: [
            { action: "Rewrite the five highest impact pages", notes: "Question and answer structure, deepened past the 300 word depth engines favour.", tool: "CMS / docs", gate: "Each page passes 300 words in Q&A structure", owner: "ourteam", source: { type: "check", ref: "thin_content" } },
            // `ref` is filled in getTracker from the snapshot's unanswered questions.
            { action: "Draft the question and answer entries", notes: "Up to 12 entries built from the real questions in the AI visibility set.", tool: "Research", gate: "Question coverage materially higher than baseline", owner: "ourteam", source: { type: "query", ref: "" } },
            { action: "Assemble the press and media page", notes: "Existing coverage collected in one place so engines see credibility signals.", tool: "CMS", gate: "Page live with all existing coverage listed", owner: "ourteam" },
            { action: "Client copy review", notes: "One consolidated review pass keeps the timeline. Brand voice preserved throughout.", tool: "Email", gate: "Feedback returned within two working days", owner: "client", touchpoint: "review" },
        ],
    },
    {
        week: 3,
        title: "Consistency and publish",
        goal: "Everything approved goes live, and the business reads consistently everywhere.",
        deliverable: "Full package published, business info consistent everywhere",
        tasks: [
            { action: "Optimise the Google Business Profile", notes: "Categories, services, description, photos and opening details brought up to standard.", tool: "GBP", gate: "Profile complete on every section", owner: "ourteam" },
            { action: "Align name, address and contact details", notes: "Consistent across the key external directories so engines see one story.", tool: "Directories", gate: "Consistent listing on three external directories", owner: "ourteam" },
            { action: "Publish approved pages and FAQs", notes: "Week 2 feedback incorporated, FAQ structured data added to the new content.", tool: "CMS", gate: "All approved content live with valid markup", owner: "ourteam" },
            { action: "Resubmit sitemap and request recrawl", notes: "Engines index the finished site, not the halfway point.", tool: "GSC", gate: "Recrawl requested on every changed page", owner: "ourteam" },
        ],
    },
    {
        week: 4,
        title: "Verify and handover",
        goal: "Prove the movement with the same measurement that set the baseline.",
        deliverable: "AI Readiness Scorecard plus the handover call",
        tasks: [
            { action: "Re-run the same assessment", notes: "Identical scan against the finished site. Before and after on all six factors.", tool: "Oura scan", gate: "Scorecard shows before and after per factor", owner: "ourteam" },
            { action: "Re-check the AI naming baseline", notes: "Ask the same engines the same questions and capture the movement.", tool: "AI assistants", gate: "Movement against the week 1 answers captured", owner: "ourteam" },
            { action: "One revision round", notes: "Anything the client wants adjusted, we adjust. Included in the package.", tool: "CMS", gate: "Requested adjustments live", owner: "ourteam" },
            { action: "Handover call and scorecard delivery", notes: "Walk through the scorecard, what changed, and what happens next.", tool: "Call", gate: "Client walked through the scorecard", owner: "client", touchpoint: "handover" },
        ],
    },
];

export function getTracker(business: Business, snapshot: ScoreSnapshot): BusinessTracker {
    const h = hashOf(business.id);
    const audit = getAudit(business, snapshot);

    // Progress point: how many tasks (in order) are already done. Ranges from
    // "just started week 1" to "mid week 3" so the portfolio shows variety.
    const doneCount = 4 + (h % 12); // 4..15 of the 21 template tasks

    // The block holding the first unfinished task.
    let currentIndex = 0;
    for (let seen = WEEK_TEMPLATE[0].tasks.length; seen <= doneCount && currentIndex < WEEK_TEMPLATE.length - 1; ) {
        currentIndex += 1;
        seen += WEEK_TEMPLATE[currentIndex].tasks.length;
    }

    // Dated around TODAY, not the capture date, so a due date and a late note
    // read as they would on a live plan in flight: the block in progress began
    // three days ago and ends tomorrow. Progress stays deterministic per
    // business; only the calendar follows the day the preview is opened.
    const now = new Date();
    const dayKey = (offset: number): string => {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const today = dayKey(0);
    const blockStart = (index: number) => -3 - 7 * (currentIndex - index);

    // The questions a content task is written from: the ones no page answers.
    const unanswered = snapshot.questions.filter((q) => !q.siteAnswers).map((q) => q.text).slice(0, 3);

    let cursor = 0;
    // One ask the client is late on, so the plan shows who it is waiting for.
    let lateAskPlaced = false;
    const weeks: TrackerWeek[] = WEEK_TEMPLATE.map((w, blockIndex) => {
        const startOffset = blockStart(blockIndex);
        const endOffset = startOffset + 4;
        const tasks: TrackerTask[] = w.tasks.map((t, i) => {
            const idx = cursor;
            cursor += 1;
            let status: TaskStatus =
                idx < doneCount ? "done" : idx === doneCount ? "in_progress" : "not_started";
            // One realistic blocked row (the BCO sheet's "Blocked (data)" case).
            if (h % 3 === 0 && w.week === 1 && i === 6 && status !== "not_started") status = "blocked";
            // Only in a block that has started: a task cannot be late in a
            // week that has not begun, so a later touchpoint keeps its block's
            // date and status.
            const lateAsk = !lateAskPlaced && t.owner === "client" && status !== "done" && startOffset <= 0;
            if (lateAsk) {
                lateAskPlaced = true;
                status = "in_progress";
            }
            const auto = AUTO_TOOLS.has(t.tool);
            const source = t.source?.type === "query"
                ? (unanswered.length ? { type: "query", ref: unanswered[0] } : undefined)
                : t.source;
            return {
                ...t,
                id: `${business.id}-w${w.week}-t${i}`,
                status,
                auto,
                source,
                sourceQueries: source?.type === "query" ? unanswered : undefined,
                notes: h % 3 === 0 && w.week === 1 && i === 6 && status === "blocked"
                    ? "Needs real review data before Review markup can ship. We do not fabricate ratings."
                    : t.notes,
                // The late ask carries a date somebody set; everything else
                // inherits its block's last day, as a live plan does.
                targetDate: lateAsk ? dayKey(-3) : dayKey(endOffset),
                targetDateSource: lateAsk ? "task" : "block",
                // The platform closes its own runs, with the reasons the
                // backend writes (flip_auto_tasks_done).
                closedBy: auto && status === "done"
                    ? {
                        assessmentId: snapshot.id,
                        at: dayKey(startOffset),
                        reason: blockIndex === 0
                            ? "The scan that produced this plan completed"
                            : `A platform run completed after this block opened on ${dayKey(startOffset)}`,
                    }
                    : undefined,
                // fixture-only until ourteam-backend handoff: Done date on tasks marked done by hand
                doneAt: !auto && status === "done" ? dayKey(Math.min(endOffset, -1)) : undefined,
            };
        });
        return {
            week: w.week,
            title: w.title,
            goal: w.goal,
            deliverable: w.deliverable,
            dates: `${dateAfter(today, startOffset)} to ${dateAfter(today, endOffset)}`,
            tasks,
        };
    });

    const currentWeek = (weeks.find((w) => w.tasks.some((t) => t.status !== "done"))?.week ?? 4) as TrackerBlock;
    // Matched by `week`, not by `weeks[currentWeek - 1]`. That arithmetic
    // assumed blocks start at 1 and run contiguously, which stopped being true
    // when Kickoff arrived as week 0 (ourteam-backend#4476): a plan whose first
    // unfinished block is Kickoff indexes `weeks[-1]`, which is `undefined`,
    // and reading `.deliverable` off it throws. The lookup holds whatever the
    // block numbers turn out to be.
    const currentBlock = weeks.find((w) => w.week === currentWeek) ?? weeks[weeks.length - 1];
    const nextDeliverable = currentBlock.deliverable;

    const week1Done = weeks[0].tasks.every((t) => t.status === "done");
    const schemaShipped = weeks[0].tasks[6].status === "done";
    const labelsShipped = weeks[0].tasks[7].status === "done";

    const missingSchemaPct = Math.min(
        95,
        Math.round(((audit.issues.find((i) => i.key === "no_schema")?.count ?? 20) / audit.pagesCrawled) * 100),
    );
    const scoreboard: ScoreboardFactor[] = [
        {
            key: "schema",
            label: "Structured data coverage",
            baseline: `${missingSchemaPct}% of pages missing markup`,
            target: "Near complete on homepage + key templates",
            result: schemaShipped ? "Markup live on homepage + key templates" : null,
            note: "Organization, LocalBusiness, FAQ, Review",
        },
        {
            key: "labels",
            label: "Page label completeness",
            baseline: `${34 + (h % 12)}% complete`,
            target: "90%+ on priority pages",
            result: labelsShipped ? "Unique labels on all priority pages" : null,
            note: "Unique titles + meta + single H1",
        },
        {
            key: "depth",
            label: "Content depth (avg words)",
            baseline: `${240 + (h % 60)} words`,
            target: "300+ where appropriate",
            result: null,
            note: "Value, not filler",
        },
        {
            key: "questions",
            label: "Question coverage",
            baseline: `${38 + (h % 10)}%`,
            target: "Materially higher",
            result: null,
            note: "Driven by the rewrites + FAQ entries",
        },
        {
            key: "visibility",
            label: "AI visibility score",
            baseline: snapshot.total !== null ? `${snapshot.total} / 100` : "Captured in week 1",
            target: "Re-measured at handover",
            result: null,
            note: "Naming baseline vs competitors",
        },
        {
            key: "indexed",
            label: "Indexed pages (GSC)",
            baseline: `${audit.pagesCrawled - 4 - (h % 6)} pages`,
            target: "Stable or rising",
            result: null,
            note: "Re-index lags publication by weeks",
        },
    ];

    const registerSource: Array<{ key: string; fixedAt: string; status: RegisterStatus; note: string }> = [
        { key: "broken_4xx", fixedAt: "Wk1 · Crawl fixes", status: week1Done ? "done" : "pending", note: "Restore or 301 every broken destination" },
        { key: "orphan_pages", fixedAt: "Wk1 · Internal linking", status: week1Done ? "done" : "pending", note: "Cheapest AEO and SEO win on the sheet" },
        { key: "no_schema", fixedAt: "Wk1 · Structured data", status: h % 3 === 0 ? "blocked" : schemaShipped ? "done" : "pending", note: h % 3 === 0 ? "Needs real review data. We do not fabricate ratings" : "Homepage + key templates first" },
        { key: "robots_missing", fixedAt: "Wk1 · Sitemap", status: week1Done ? "done" : "pending", note: "Declared and resubmitted so recrawl starts immediately" },
        { key: "missing_meta", fixedAt: "Wk1 · Page labels", status: labelsShipped ? "done" : "pending", note: "Unique description per priority page" },
        { key: "dup_titles", fixedAt: "Wk1 · Page labels", status: labelsShipped ? "done" : "pending", note: "One clear title per page, brand last" },
        { key: "redirect_chains", fixedAt: "Wk1 · Crawl fixes", status: week1Done ? "done" : "pending", note: "Every redirect collapsed to one hop" },
        { key: "thin_content", fixedAt: "Wk2 · Content", status: currentWeek > 2 ? "done" : "pending", note: "Expanded past 300 words or folded into a parent" },
        { key: "cwv", fixedAt: "Flagged", status: "out_of_scope", note: "Quick wins noted. Deep performance work is a separate engagement" },
    ];
    const fixRegister: FixRegisterRow[] = registerSource.map((r) => {
        const issue = audit.issues.find((i) => i.key === r.key);
        return {
            issueKey: r.key,
            issue: issue?.label ?? r.key,
            detectedWhere: r.key === "cwv" ? "Page speed (PSI)" : r.key === "no_schema" ? "Schema audit" : "Crawl audit",
            fixedAt: r.fixedAt,
            status: r.status,
            note: r.note,
        };
    });

    return {
        reference: `OT-2026-${business.name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 3)}-001`,
        packageName: "Website AI Readiness Package",
        window: `${dateAfter(today, blockStart(0))} to ${dateAfter(today, blockStart(WEEK_TEMPLATE.length - 1) + 4)}`,
        currentWeek,
        nextDeliverable,
        scoreboard,
        weeks,
        fixRegister,
    };
}

/** Overall progress: done / total across the four weeks. */
export function trackerProgress(tracker: BusinessTracker): { done: number; total: number } {
    const all = tracker.weeks.flatMap((w) => w.tasks);
    return { done: all.filter((t) => t.status === "done").length, total: all.length };
}
